import React, { useState, useEffect } from 'react';
import './PrivacyShowdown.css';
import { useProgress } from '../contexts/ProgressContext';
import UnderTheHood from './UnderTheHood';

const SCENARIOS = [
  {
    id: 1,
    title: 'Send Funds',
    description: 'You need to send 100 tokens to a friend. What information do you share?',
    options: [
      {
        id: 'a',
        label: 'Full transaction details',
        details: 'Reveal: sender, receiver, amount, timestamp, memo',
        privacyScore: 0,
        feedback: 'Everyone can see who sent what to whom! No privacy at all. Traditional blockchain transparency exposes all your financial activity.'
      },
      {
        id: 'b',
        label: 'Public addresses only',
        details: 'Reveal: sender address, receiver address, amount',
        privacyScore: 30,
        feedback: 'Better, but addresses can be tracked across transactions. People can build a profile of your spending habits by analyzing the blockchain.'
      },
      {
        id: 'c',
        label: 'ZK proof of ownership',
        details: 'Prove: you have funds without revealing amount or addresses',
        privacyScore: 100,
        feedback: 'Perfect! With zero-knowledge, you prove you can make the payment without exposing who, what, or how much. Maximum privacy!'
      }
    ]
  },
  {
    id: 2,
    title: 'Prove Your Age',
    description: 'You need to prove you\'re over 18 to access a service. What do you share?',
    options: [
      {
        id: 'a',
        label: 'Full birthdate and ID',
        details: 'Reveal: full name, birthdate, ID number, photo',
        privacyScore: 0,
        feedback: 'Way too much information! Now they know your exact age, full identity, and can track you. This data could be leaked or misused.'
      },
      {
        id: 'b',
        label: 'Just your age',
        details: 'Reveal: I am 25 years old',
        privacyScore: 50,
        feedback: 'Better than full details, but still reveals more than necessary. Your exact age can be used for targeted advertising or profiling.'
      },
      {
        id: 'c',
        label: 'ZK age proof',
        details: 'Prove: age > 18 without revealing exact age or identity',
        privacyScore: 100,
        feedback: 'Excellent! You proved eligibility without exposing your birth date, exact age, or identity. The service only learns you meet the requirement!'
      }
    ]
  },
  {
    id: 3,
    title: 'Vote in DAO',
    description: 'You want to vote on a proposal in a decentralized organization. How do you prove you\'re a member?',
    options: [
      {
        id: 'a',
        label: 'Show your account and vote',
        details: 'Reveal: wallet address, vote choice, voting power',
        privacyScore: 0,
        feedback: 'Your vote is public and linked to your identity! People can see how you voted and pressure you. Not democratic.'
      },
      {
        id: 'b',
        label: 'Anonymous account',
        details: 'Use separate account, but votes still visible',
        privacyScore: 40,
        feedback: 'Helps privacy, but sophisticated analysis can still link votes to identities. Plus, managing multiple accounts is cumbersome.'
      },
      {
        id: 'c',
        label: 'ZK membership proof',
        details: 'Prove: membership without revealing identity or vote',
        privacyScore: 100,
        feedback: 'Perfect! You voted privately while proving you\'re a legitimate member. This is true democratic privacy—your vote counted but stayed secret!'
      }
    ]
  },
  {
    id: 4,
    title: 'Prove Creditworthiness',
    description: 'You want a loan and need to prove you qualify. What financial data do you share?',
    options: [
      {
        id: 'a',
        label: 'Full financial history',
        details: 'Reveal: income, expenses, all accounts, credit score',
        privacyScore: 0,
        feedback: 'Total financial surveillance! The lender sees everything about your money. This data could be sold or leaked to third parties.'
      },
      {
        id: 'b',
        label: 'Just credit score',
        details: 'Reveal: credit score number (e.g., 720)',
        privacyScore: 60,
        feedback: 'Better, but your score reveals a lot about your financial behavior and can be used for price discrimination.'
      },
      {
        id: 'c',
        label: 'ZK credit range proof',
        details: 'Prove: score above threshold without exact number',
        privacyScore: 100,
        feedback: 'Ideal! You proved you qualify for the loan without exposing your exact score, income, or financial history. Privacy-preserving finance!'
      }
    ]
  },
  {
    id: 5,
    title: 'Access Premium Content',
    description: 'You subscribed to premium content. How do you prove your subscription?',
    options: [
      {
        id: 'a',
        label: 'Login with email/password',
        details: 'Reveal: email, browsing history tracked via cookies',
        privacyScore: 20,
        feedback: 'Your identity is logged and your activity tracked across sessions. The platform builds a profile of your interests and habits.'
      },
      {
        id: 'b',
        label: 'NFT subscription in wallet',
        details: 'Show: wallet address holding subscription NFT',
        privacyScore: 50,
        feedback: 'Better than email, but your wallet address is exposed. All your other activities on that address can be analyzed.'
      },
      {
        id: 'c',
        label: 'ZK subscription proof',
        details: 'Prove: valid subscription without revealing identity',
        privacyScore: 100,
        feedback: 'Excellent! You accessed content without exposing your identity or wallet. True privacy—enjoy content without surveillance!'
      }
    ]
  }
];

const PrivacyShowdown = () => {
  const { updateGameProgress, getProgress } = useProgress();
  const [currentScenario, setCurrentScenario] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [totalScore, setTotalScore] = useState(100);
  const [scenarioScores, setScenarioScores] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [gameComplete, setGameComplete] = useState(false);

  const scenario = SCENARIOS[currentScenario];

  useEffect(() => {
    const saved = getProgress('games', 'privacy-showdown');
    if (!saved) {
      updateGameProgress('privacy-showdown', null, 1, false);
    }
  }, [getProgress, updateGameProgress]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setShowFeedback(true);
    
    // Calculate privacy cost
    const privacyLost = 100 - option.privacyScore;
    const newScore = Math.max(0, totalScore - (privacyLost / 2)); // Scale the loss
    setTotalScore(newScore);
    setScenarioScores([...scenarioScores, { scenario: scenario.title, score: option.privacyScore }]);
  };

  const nextScenario = () => {
    if (currentScenario < SCENARIOS.length - 1) {
      setCurrentScenario(currentScenario + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      setGameComplete(true);
      const finalScore = Math.round((scenarioScores.reduce((sum, s) => sum + s.score, 0) + (selectedOption?.privacyScore || 0)) / SCENARIOS.length);
      updateGameProgress('privacy-showdown', finalScore, SCENARIOS.length, true, 'Privacy Protection');
    }
  };

  const restartGame = () => {
    setCurrentScenario(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setTotalScore(100);
    setScenarioScores([]);
    setGameComplete(false);
  };

  const getPrivacyLevel = (score) => {
    if (score >= 80) return { label: 'Excellent Privacy', color: '#10b981', icon: 'shield' };
    if (score >= 60) return { label: 'Good Privacy', color: '#60a5fa', icon: 'shield' };
    if (score >= 40) return { label: 'Moderate Privacy', color: '#f59e0b', icon: 'alert' };
    return { label: 'Poor Privacy', color: '#ef4444', icon: 'warning' };
  };

  const privacyLevel = getPrivacyLevel(totalScore);

  return (
    <div className="privacy-showdown">
      {showIntro && (
        <div className="ps-modal-overlay">
          <div className="ps-modal">
            <div className="ps-modal-header">
              <h2>Protect Your Privacy!</h2>
              <button className="ps-close" onClick={() => setShowIntro(false)}>×</button>
            </div>
            <div className="ps-modal-body">
              <p>
                Each round shows a web3 situation. Decide what info to share. The less you reveal, 
                the safer you stay. See how zero-knowledge keeps secrets locked away—even when you 
                need to prove something.
              </p>
              <div className="ps-intro-features">
                <div className="ps-feature">
                  <h4>Real Scenarios</h4>
                  <p>Make privacy decisions in situations you'll face in web3 and digital life.</p>
                </div>
                <div className="ps-feature">
                  <h4>Learn By Doing</h4>
                  <p>See the immediate consequences of your privacy choices with detailed feedback.</p>
                </div>
                <div className="ps-feature">
                  <h4>Privacy Score</h4>
                  <p>Track your privacy level as you make decisions. Minimize exposure, maximize privacy!</p>
                </div>
              </div>
            </div>
            <div className="ps-modal-footer">
              <button className="ps-primary" onClick={() => setShowIntro(false)}>Start Protecting</button>
            </div>
          </div>
        </div>
      )}

      {!gameComplete ? (
        <>
          <div className="ps-header">
            <h1>Spot the Leak</h1>
            <div className="ps-progress">
              Scenario {currentScenario + 1} of {SCENARIOS.length}
            </div>
          </div>

          <div className="ps-privacy-meter">
            <div className="ps-meter-label">
              <span>Privacy Level</span>
              <span className="ps-score">{Math.round(totalScore)}%</span>
            </div>
            <div className="ps-meter-bar">
              <div 
                className="ps-meter-fill" 
                style={{ 
                  width: `${totalScore}%`,
                  background: totalScore >= 70 ? '#10b981' : totalScore >= 40 ? '#f59e0b' : '#ef4444'
                }}
              />
            </div>
            <div className="ps-meter-status" style={{ color: privacyLevel.color }}>
              {privacyLevel.label}
            </div>
          </div>

          <div className="ps-scenario">
            <div className="ps-scenario-header">
              <h2>{scenario.title}</h2>
              <p>{scenario.description}</p>
            </div>

            <div className="ps-options">
              {scenario.options.map((option) => (
                <div
                  key={option.id}
                  className={`ps-option ${selectedOption?.id === option.id ? 'selected' : ''} ${showFeedback && selectedOption?.id !== option.id ? 'dimmed' : ''}`}
                  onClick={() => !showFeedback && handleOptionSelect(option)}
                >
                  <div className="ps-option-header">
                    <h3>{option.label}</h3>
                    {showFeedback && (
                      <div 
                        className="ps-privacy-badge"
                        style={{
                          background: option.privacyScore >= 80 ? 'rgba(16,185,129,0.2)' : 
                                      option.privacyScore >= 50 ? 'rgba(245,158,11,0.2)' : 
                                      'rgba(239,68,68,0.2)',
                          border: option.privacyScore >= 80 ? '1px solid rgba(16,185,129,0.3)' : 
                                  option.privacyScore >= 50 ? '1px solid rgba(245,158,11,0.3)' : 
                                  '1px solid rgba(239,68,68,0.3)',
                          color: option.privacyScore >= 80 ? '#10b981' : 
                                 option.privacyScore >= 50 ? '#f59e0b' : 
                                 '#ef4444'
                        }}
                      >
                        Privacy: {option.privacyScore}%
                      </div>
                    )}
                  </div>
                  <p className="ps-option-details">{option.details}</p>
                  
                  {showFeedback && selectedOption?.id === option.id && (
                    <div className="ps-feedback">
                      <strong>Result:</strong>
                      <p>{option.feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {showFeedback && (
              <div className="ps-action">
                <button className="ps-next-btn" onClick={nextScenario}>
                  {currentScenario < SCENARIOS.length - 1 ? 'Next Scenario' : 'See Final Results'}
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="ps-completion">
          <div className="ps-completion-header">
            <h1>Spot the Leak Complete!</h1>
            <div className="ps-final-score">
              <div className="ps-score-circle" style={{ borderColor: privacyLevel.color }}>
                <span className="ps-score-value">{Math.round((scenarioScores.reduce((sum, s) => sum + s.score, 0) + (selectedOption?.privacyScore || 0)) / SCENARIOS.length)}%</span>
                <span className="ps-score-label">Privacy Score</span>
              </div>
            </div>
          </div>

          <div className="ps-completion-content">
            <div className="ps-insight">
              <h3>What You Learned</h3>
              <p>
                With traditional sharing, others see everything about you—transactions, identity, 
                financial details, and more. Using zero-knowledge proofs, you can prove what's necessary 
                while keeping private info hidden.
              </p>
              <p>
                Spot the Leak taught you to think like a privacy pro! In the real web3 world, 
                every data point you share can be tracked, analyzed, and potentially misused. 
                Zero-knowledge technology gives you control—prove what you need, hide what you don't.
              </p>
            </div>

            <div className="ps-scenario-breakdown">
              <h3>Your Decisions</h3>
              {scenarioScores.map((item, index) => (
                <div key={index} className="ps-scenario-score">
                  <span className="ps-scenario-name">{item.scenario}</span>
                  <div className="ps-score-bar">
                    <div 
                      className="ps-score-fill" 
                      style={{ 
                        width: `${item.score}%`,
                        background: item.score >= 80 ? '#10b981' : item.score >= 50 ? '#f59e0b' : '#ef4444'
                      }}
                    />
                  </div>
                  <span className="ps-scenario-percent">{item.score}%</span>
                </div>
              ))}
            </div>

            <div className="ps-completion-actions">
              <button className="ps-restart-btn" onClick={restartGame}>
                Play Again
              </button>
            </div>

            <UnderTheHood>
              <p><strong>Zero-Knowledge Proofs:</strong> Each scenario demonstrates how ZK proofs allow you to prove statements (like "I have funds" or "I'm over 18") without revealing the underlying data.</p>
              <p><strong>Privacy Score Calculation:</strong> Scores are based on information entropy - the less information revealed, the higher the privacy score. ZK proofs achieve 100% by revealing zero information while still proving the statement.</p>
              <p><strong>Miden Implementation:</strong> Miden uses STARK-based zero-knowledge proofs to enable these privacy-preserving transactions. The VM executes operations that can be proven correct without exposing inputs.</p>
            </UnderTheHood>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyShowdown;

